import { Boat as UiBoat, Propulsion } from '../models/boat.model';
import { BoatDto, AddBoatRequest, PropulsionRequest } from '../api/models/boat.dto';
import { normalizeImageUrl } from '../utils/image.util';

export class BoatMapper {
  /**
   * Transforms the backend BoatDto into our UI Boat model.
   * Isolates the UI from nullable structures, flat photos, and enum naming mismatches.
   */
  static fromDto(dto: BoatDto): UiBoat {
    const specs: Record<string, any> = {};
    if (dto.engineHours != null) specs['engineHours'] = dto.engineHours;
    if (dto.totalPower != null) specs['totalPower'] = dto.totalPower;
    if (dto.overallLength != null) specs['overallLength'] = dto.overallLength;
    if (dto.beam != null) specs['beam'] = dto.beam;
    if (dto.maxDraft != null) specs['maxDraft'] = dto.maxDraft;
    if (dto.hullMaterial) specs['hullMaterial'] = dto.hullMaterial;
    if (dto.hullShape) specs['hullShape'] = dto.hullShape;
    if (dto.freshWaterTank != null) specs['freshWaterTank'] = dto.freshWaterTank;
    if (dto.fuelTank != null) specs['fuelTank'] = dto.fuelTank;
    if (dto.holdingTank != null) specs['holdingTank'] = dto.holdingTank;
    if (dto.guestCabin != null) specs['guestCabins'] = dto.guestCabin;
    if (dto.guestHead != null) specs['guestHeads'] = dto.guestHead;
    if (dto.crewCabin != null) specs['crewCabins'] = dto.crewCabin;
    if (dto.crewHead != null) specs['crewHeads'] = dto.crewHead;

    return {
      id: dto.boatId?.toString() || '',
      productId: dto.productId || undefined,
      make: dto.boatName?.split(' ')[0] || '',
      model: dto.boatModel || null,
      boatTypeName: dto.boatType?.boatTypeName || dto.boatTypeName || '',
      name: dto.boatName,
      year: dto.boatYear || null,
      length: dto.overallLength || null,
      condition: dto.condition || 'Used',
      price: dto.price,
      standardCost: dto.standardCost ?? null,
      stockCount: dto.boatCount,
      isActive: dto.isActive !== false,
      status: dto.status?.statusName || (dto as any).statusName || '-',
      lastModifiedBy: dto.modifiedBy
        ? `${dto.modifiedBy.firstName || ''} ${dto.modifiedBy.lastName || ''}`.trim()
        : '-',
      updatedDate: dto.updatedDate || '-',
      brokerId: dto.brokerId,
      boatTypeId: dto.boatTypeId || undefined,
      description: dto.boatDescription || '',
      hullMaterial: dto.hullMaterial || null,
      propulsions: (dto.propulsions || []).map((p: any) => ({
        propulsionId: p.propulsionId ?? undefined,
        engineMake: p.engineMake || null,
        engineModel: p.engineModel || null,
        engineYear: p.engineYear || null,
        totalPower: p.totalPower || null,
        engineType: p.engineType || null,
        driveType: p.driveType || null,
        fuelType: p.fuelType || null,
      })),
      specs: specs,
      videoUrl: dto.videoUrl || '',
      boatDetails: dto.boatDetails || null,
      featureIds:
        dto.boatFeatures
          ?.map((f: any) => f.featureId)
          .filter((id: any): id is number => typeof id === 'number') || [],
      boatPhotos: dto.boatPhotos || (dto as any).media || (dto as any).mediaObject || [],
      photoUrl: normalizeImageUrl(this.findThumbnail(dto, 'url')),
      photoThumbnailUrl: normalizeImageUrl(this.findThumbnail(dto, 'thumb')),
      photoMediumUrl: normalizeImageUrl(this.findThumbnail(dto, 'medium')),
      thumbnailPhoto: normalizeImageUrl(
        dto.thumbnailPhoto || this.findThumbnail(dto, 'thumb') || '',
      ),
      media: (dto as any).media || null,
      productLocations: dto.productLocations || [],
    } as any;
  }

  /**
   * Maps DTO to Public BoatProduct model.
   * Higher-level detail for catalog and public detail pages.
   */
  static toBoatProduct(dto: BoatDto): any {
    const boat = this.fromDto(dto);

    return {
      id: boat.id,
      type: 'boat',
      name: boat.name,
      description: boat.description,
      price: boat.price,
      stockCount: boat.stockCount,
      isActive: boat.isActive,
      brokerId: boat.brokerId,
      status: boat.status,
      boatTypeName: boat.boatTypeName,
      details: {
        type: dto.boatType?.boatTypeName,
        year: boat.year,
        hullMaterial: boat.hullMaterial,
      },
      propulsions: (boat.propulsions || []).map((p) => ({
        make: p.engineMake,
        model: p.engineModel,
        year: p.engineYear,
        power: p.totalPower,
        type: p.engineType,
      })),
      features: this.buildFeatureGroups(dto.boatFeatures || []),
      specifications: Object.entries(boat.specs || {}).map(([label, value]) => ({
        label: label.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()), // camelCase to Title Case
        value: value,
      })),
      boatPhotos: boat.boatPhotos,
      photoUrl: boat.photoUrl,
      photoThumbnailUrl: boat.photoThumbnailUrl,
      photoMediumUrl: boat.photoMediumUrl,
      thumbnailPhoto: boat.thumbnailPhoto,
    } as any;
  }

  private static buildFeatureGroups(boatFeatures: any[]): any[] {
    const groups: Record<string, string[]> = {};
    boatFeatures.forEach((bf) => {
      const f = bf.feature;
      if (!f) return;
      const type = f.featureType || 'General';
      if (!groups[type]) groups[type] = [];
      groups[type].push(f.featureName);
    });
    return Object.entries(groups).map(([name, items]) => ({
      groupName: name,
      features: items,
    }));
  }

  static buildAddRequest(
    existing: BoatDto,
    patch: Partial<UiBoat>,
    brokerId: number,
    staffId?: number,
  ): AddBoatRequest {
    return this.buildEditRequest(existing, patch, brokerId, staffId);
  }

  static buildEditRequest(
    existing: BoatDto,
    patch: Partial<UiBoat>,
    brokerId: number,
    staffId?: number,
  ): AddBoatRequest {
    if (!brokerId || brokerId <= 0) {
      throw new Error(`[BoatMapper] Invalid brokerId: ${brokerId}`);
    }

    const parseNullableNum = (val: any, fallback: any): number | null => {
      if (val === undefined) {
        if (fallback == null || fallback === '') return null;
        const fn = Number(fallback);
        return isNaN(fn) ? null : fn;
      }
      if (val === null || val === '') return null;
      const n = Number(val);
      return isNaN(n) ? null : n;
    };

    const existingPropulsions: PropulsionRequest[] = (existing.propulsions || []).map((p: any) => ({
      propulsionId: p.propulsionId && p.propulsionId > 0 ? p.propulsionId : undefined,
      engineMake: p.engineMake || '',
      engineModel: p.engineModel || '',
      engineYear: parseNullableNum(p.engineYear, null),
      totalPower: parseNullableNum(p.totalPower, null),
      engineType: p.engineType || '',
      driveType: p.driveType || '',
      fuelType: p.fuelType || '',
    }));

    const mergedPropulsions: PropulsionRequest[] =
      patch.propulsions !== undefined
        ? patch.propulsions.map((p) => ({
            propulsionId: p.propulsionId && p.propulsionId > 0 ? p.propulsionId : undefined,
            engineMake: p.engineMake || '',
            engineModel: p.engineModel || '',
            engineYear: parseNullableNum(p.engineYear, null),
            totalPower: p.totalPower != null ? Number(p.totalPower) : null,
            engineType: p.engineType || '',
            driveType: p.driveType || '',
            fuelType: p.fuelType || '',
          }))
        : existingPropulsions;

    const resolvedFeatureIds: number[] =
      patch.featureIds ??
      existing.boatFeatures
        ?.map((f: any) => f.featureId)
        .filter((id: any): id is number => typeof id === 'number') ??
      [];

    return {
      brokerId: brokerId,
      productTypeId: patch.productTypeId ?? 1,
      boatName: patch.name || existing.boatName || 'Unnamed',
      boatTypeId: parseNullableNum(patch.boatTypeId, existing.boatTypeId),
      condition: patch.condition || existing.condition || null,
      price: Number(patch.price ?? existing.price ?? 0),
      standardCost: Number(patch.standardCost ?? existing.standardCost ?? 0),
      boatCount: Number(patch.stockCount ?? existing.boatCount ?? 1),
      boatDescription: patch.description !== undefined ? (patch.description || null) : (existing.boatDescription || null),
      boatDetails: patch.boatDetails !== undefined ? (patch.boatDetails || null) : (existing.boatDetails || null),
      boatModel: patch.model !== undefined ? (patch.model || null) : (existing.boatModel || null),
      boatYear: parseNullableNum(patch.specs?.['boatYear'] !== undefined ? patch.specs['boatYear'] : patch.year, existing.boatYear),
      engineHours: parseNullableNum(patch.specs?.['engineHours'], existing.engineHours),
      totalPower: parseNullableNum(patch.specs?.['totalPower'], existing.totalPower),
      overallLength:
        patch.length || parseNullableNum(patch.specs?.['overallLength'], existing.overallLength),
      beam: parseNullableNum(patch.specs?.['beam'], existing.beam),
      maxDraft: parseNullableNum(patch.specs?.['maxDraft'], existing.maxDraft),
      hullMaterial:
        patch.specs?.['hullMaterial'] !== undefined ? patch.specs['hullMaterial'] || null : (patch.hullMaterial !== undefined ? patch.hullMaterial || null : existing.hullMaterial || null),
      hullShape: patch.specs?.['hullShape'] !== undefined ? patch.specs['hullShape'] || null : existing.hullShape || null,
      freshWaterTank: parseNullableNum(patch.specs?.['freshWaterTank'], existing.freshWaterTank),
      fuelTank: parseNullableNum(patch.specs?.['fuelTank'], existing.fuelTank),
      holdingTank: parseNullableNum(patch.specs?.['holdingTank'], existing.holdingTank),
      guestCabin: parseNullableNum(patch.specs?.['guestCabins'], existing.guestCabin),
      guestHead: parseNullableNum(patch.specs?.['guestHeads'], existing.guestHead),
      crewCabin: parseNullableNum(patch.specs?.['crewCabins'], existing.crewCabin),
      crewHead: parseNullableNum(patch.specs?.['crewHeads'], existing.crewHead),
      videoUrl: patch.videoUrl !== undefined ? patch.videoUrl : existing.videoUrl || '',
      otherDetails: patch.boatDetails !== undefined ? (patch.boatDetails || '') : (existing.boatDetails || ''),
      isActive: patch.isActive ?? existing.isActive ?? true,
      featureIds: resolvedFeatureIds,
      propulsions: mergedPropulsions,
      staffId: staffId || null,
      modifiedBy: staffId || null,
    };
  }

  private static findThumbnail(dto: BoatDto, type: 'url' | 'thumb' | 'medium'): string {
    const thumbPhoto = dto.thumbnailPhoto;
    if (!thumbPhoto) return '';
    if (type === 'thumb') return thumbPhoto.photoThumbnailUrl || thumbPhoto.photoUrl || '';
    if (type === 'medium') return thumbPhoto.photoMediumUrl || thumbPhoto.photoUrl || '';
    return thumbPhoto.photoUrl || '';
  }
}
